// src/pages/eglises/ListeEglises.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
    Row, Col, Card, Spinner, Modal, Alert, Button, Badge,
    Table, Pagination, Form, InputGroup
} from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../layouts/Header';
import Footer from '../../layouts/Footer';
import { COLORS } from '../../constants/colors';

// ===== STYLES =====
const styles = `
  .dashboard-styled {
    background: #f4f6f9;
    min-height: 100vh;
  }

  .header-modern {
    background: white;
    padding: 1.2rem 1.8rem;
    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
    border-left: 4px solid ${COLORS.secondary};
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
  }
  .header-left {
    display: flex;
    align-items: center;
    gap: 1rem;
  }
  .header-icon-wrapper {
    width: 48px;
    height: 48px;
    background: ${COLORS.primary};
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    color: white;
    font-size: 1.4rem;
  }
  .gradient-text {
    color: #1e293b;
    font-size: 1.3rem;
    font-weight: 700;
    margin: 0;
  }
  .badge-modern {
    background: #f1f4f9;
    color: #475569;
    padding: 0.3rem 0.8rem;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .badge-modern i {
    color: ${COLORS.secondary};
  }

  .btn-modern-outline {
    border: 1px solid #d1d5db;
    background: white;
    padding: 0.4rem 1.2rem;
    border-radius: 4px;
    font-size: 0.85rem;
    font-weight: 500;
    color: #4b5563;
    transition: all 0.2s;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .btn-modern-outline:hover {
    border-color: ${COLORS.secondary};
    color: ${COLORS.secondary};
    background: #fafafa;
  }
  .btn-modern-primary {
    background: ${COLORS.primary};
    border: none;
    padding: 0.4rem 1.2rem;
    border-radius: 4px;
    font-size: 0.85rem;
    font-weight: 500;
    color: white;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    transition: all 0.2s;
  }
  .btn-modern-primary:hover {
    background: ${COLORS.primaryDark};
    box-shadow: 0 4px 10px rgba(41,90,167,0.3);
  }

  /* KPI Cards */
  .kpi-card {
    background: white;
    border-radius: 4px;
    padding: 1rem;
    display: flex;
    align-items: center;
    gap: 1rem;
    box-shadow: 0 2px 6px rgba(0,0,0,0.04);
    transition: all 0.2s ease;
    height: 100%;
    border-left: 3px solid;
  }
  .kpi-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(0,0,0,0.06);
  }
  .kpi-primary { border-left-color: ${COLORS.primary}; }
  .kpi-success { border-left-color: ${COLORS.success}; }
  .kpi-warning { border-left-color: ${COLORS.warning}; }
  .kpi-danger { border-left-color: ${COLORS.danger}; }
  .kpi-info { border-left-color: ${COLORS.info}; }
  .kpi-purple { border-left-color: #8b5cf6; }
  .kpi-pink { border-left-color: #ec4899; }

  .kpi-icon {
    width: 44px;
    height: 44px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.2rem;
  }
  .kpi-primary .kpi-icon { background: ${COLORS.primary}15; color: ${COLORS.primary}; }
  .kpi-success .kpi-icon { background: ${COLORS.success}15; color: ${COLORS.success}; }
  .kpi-warning .kpi-icon { background: ${COLORS.warning}15; color: ${COLORS.warning}; }
  .kpi-danger .kpi-icon { background: ${COLORS.danger}15; color: ${COLORS.danger}; }
  .kpi-info .kpi-icon { background: ${COLORS.info}15; color: ${COLORS.info}; }
  .kpi-purple .kpi-icon { background: #8b5cf615; color: #8b5cf6; }
  .kpi-pink .kpi-icon { background: #ec489915; color: #ec4899; }

  .kpi-content { flex: 1; }
  .kpi-label {
    font-size: 0.7rem;
    text-transform: uppercase;
    color: #6c757d;
    font-weight: 600;
    letter-spacing: 0.5px;
  }
  .kpi-value {
    font-size: 1.3rem;
    font-weight: 700;
    margin: 0.1rem 0 0 0;
    color: #1e293b;
  }

  /* Filter card */
  .filter-card {
    border: none;
    border-radius: 4px;
    background: ${COLORS.primary};
    box-shadow: 0 2px 6px rgba(0,0,0,0.04);
  }
  .filter-card .form-control,
  .filter-card .form-select {
    border-radius: 4px;
    border: none;
    padding: 0.35rem 0.7rem;
    font-size: 0.8rem;
    background: rgba(255,255,255,0.95);
  }
  .filter-card .form-control:focus,
  .filter-card .form-select:focus {
    box-shadow: 0 0 0 3px rgba(255,255,255,0.2);
  }

  /* Table */
  .table-card {
    border: none;
    border-radius: 4px;
    background: white;
    box-shadow: 0 2px 6px rgba(0,0,0,0.04);
    overflow: hidden;
  }
  .table-header-secondary {
    background: ${COLORS.secondary};
    color: white;
    padding: 0.6rem 1rem;
  }
  .table-modern thead th {
    background: #f8fafc;
    color: #475569;
    font-weight: 600;
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    border-bottom: 1px solid #e2e8f0;
    white-space: nowrap;
  }
  .table-modern tbody tr {
    transition: all 0.2s ease;
  }
  .table-modern tbody tr:hover {
    background: #f8fafc;
  }
  .reference-badge {
    background: #f1f4f9;
    padding: 0.2rem 0.6rem;
    border-radius: 4px;
    font-size: 0.7rem;
    font-family: monospace;
    color: #334155;
  }
  .church-avatar {
    width: 34px;
    height: 34px;
    border-radius: 4px;
    background: #f1f4f9;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1rem;
    color: ${COLORS.primary};
    border: 1px solid #e2e8f0;
  }
  .action-btn {
    width: 28px;
    height: 28px;
    border-radius: 4px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: none;
    transition: all 0.2s ease;
    font-size: 0.9rem;
  }
  .action-btn:hover {
    transform: scale(1.1);
    box-shadow: 0 4px 8px rgba(0,0,0,0.08);
  }
  .action-btn-view { background: ${COLORS.primary}15; color: ${COLORS.primary}; }
  .action-btn-view:hover { background: ${COLORS.primary}; color: white; }
  .action-btn-edit { background: ${COLORS.secondary}15; color: ${COLORS.secondary}; }
  .action-btn-edit:hover { background: ${COLORS.secondary}; color: white; }
  .action-btn-delete { background: ${COLORS.danger}15; color: ${COLORS.danger}; }
  .action-btn-delete:hover { background: ${COLORS.danger}; color: white; }
  .action-btn-delete:disabled { opacity: 0.3; cursor: not-allowed; }

  /* Pagination */
  .pagination-modern .page-link {
    border: none;
    margin: 0 2px;
    border-radius: 4px;
    color: #64748b;
    font-size: 0.8rem;
    padding: 0.3rem 0.7rem;
    transition: all 0.2s ease;
  }
  .pagination-modern .page-link:hover {
    background: ${COLORS.primary};
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(41,90,167,0.2);
  }
  .pagination-modern .page-item.active .page-link {
    background: ${COLORS.primary};
    color: white;
    box-shadow: 0 4px 8px rgba(41,90,167,0.3);
  }

  /* Modal */
  .delete-modal .modal-content {
    border: none;
    border-radius: 4px;
    overflow: hidden;
    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
  }
  .delete-modal .modal-header {
    border-bottom: none;
    padding: 1.2rem 1.5rem 0.5rem;
  }
  .delete-modal .modal-footer {
    border-top: none;
    padding: 0.5rem 1.5rem 1.2rem;
  }

  .delete-icon-circle {
    width: 56px;
    height: 56px;
    border-radius: 4px;
    background: ${COLORS.danger}15;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.8rem;
    color: ${COLORS.danger};
  }

  /* Loading */
  .loading-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 60vh;
  }
  .loading-ring-wrapper {
    position: relative;
    width: 70px;
    height: 70px;
  }
  .loading-ring {
    position: absolute;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    border: 3px solid #e9edf2;
    border-top-color: ${COLORS.primary};
    animation: spin 0.8s linear infinite;
  }
  .loading-ring-inner {
    position: absolute;
    top: 8px;
    left: 8px;
    right: 8px;
    bottom: 8px;
    border-radius: 50%;
    border: 2px solid #e9edf2;
    border-bottom-color: ${COLORS.secondary};
    animation: spin 0.6s linear infinite reverse;
  }
  @keyframes spin { to { transform: rotate(360deg); } }

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(6px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .fade-in {
    animation: fadeIn 0.3s ease-out;
  }

  @media (max-width: 768px) {
    .header-modern {
      flex-direction: column;
      align-items: stretch;
      gap: 0.8rem;
      padding: 1rem;
    }
    .header-left {
      flex-wrap: wrap;
    }
  }
`;

export default function ListeEglises() {
    const navigate = useNavigate();
    const location = useLocation();

    const [eglises, setEglises] = useState([]);
    const [stats, setStats] = useState({
        total: 0,
        totalTemples: 0,
        provinces: 0,
        villes: 0,
        eglisesAvecTemples: 0,
        totalCaissesNationales: 0
    });
    const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [provinceFilter, setProvinceFilter] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const [timestamp, setTimestamp] = useState(Date.now());
    const [shouldReload, setShouldReload] = useState(false);

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [egliseToDelete, setEgliseToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');

    const provinces = [
        'Estuaire', 'Haut-Ogooué', 'Moyen-Ogooué', 'Ngounié',
        'Nyanga', 'Ogooué-Ivindo', 'Ogooué-Lolo', 'Ogooué-Maritime', 'Woleu-Ntem'
    ];

    // Récupération des droits
    const userStr = localStorage.getItem('user');
    let isSuperAdmin = false;
    let isAdmin = false;
    if (userStr) {
        try {
            const user = JSON.parse(userStr);
            isAdmin = user.role === 'Administrateur';
            isSuperAdmin = user.role === 'Super Administrateur';
        } catch (e) { }
    }

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const forceReload = params.get('force');
        if (forceReload) {
            setRefreshKey(prev => prev + 1);
            setTimestamp(Date.now());
            setShouldReload(true);
            navigate('/eglises/sieges', { replace: true });
        }
    }, [location, navigate]);

    useEffect(() => {
        if (shouldReload) {
            window.location.reload();
        }
    }, [shouldReload]);

    const fetchEglises = useCallback(async () => {
        setLoading(true);
        try {
            const userStr = localStorage.getItem('user');
            if (!userStr) {
                navigate('/pages/signin2');
                return;
            }
            const user = JSON.parse(userStr);
            const userId = user.id || user.id_utilisateur;

            const cacheBuster = Date.now();
            let url = `https://josephsbusiness.com/api/eglises.php?user_id=${userId}&page=${currentPage}&limit=${itemsPerPage}&parent=siege&_=${cacheBuster}`;
            if (searchTerm) url += `&search=${encodeURIComponent(searchTerm)}`;
            if (provinceFilter) url += `&province=${encodeURIComponent(provinceFilter)}`;

            const response = await fetch(url, {
                headers: {
                    'Accept': 'application/json',
                    'Cache-Control': 'no-cache, no-store, must-revalidate',
                    'Pragma': 'no-cache',
                    'Expires': '0'
                }
            });
            const result = await response.json();

            if (result.success) {
                setEglises(result.eglises || []);
                const eglisesData = result.eglises || [];
                const totalTemples = eglisesData.reduce((acc, e) => acc + (e.nb_filiales || 0), 0);
                const provincesList = [...new Set(eglisesData.map(e => e.province).filter(Boolean))];
                const villesList = [...new Set(eglisesData.map(e => e.ville).filter(Boolean))];
                const eglisesAvecTemples = eglisesData.filter(e => (e.nb_filiales || 0) > 0).length;
                const caissesNationales = eglisesData.filter(e => e.caisse === 1).length;

                setStats({
                    total: result.stats?.sieges || 0,
                    totalTemples,
                    provinces: provincesList.length,
                    villes: villesList.length,
                    eglisesAvecTemples,
                    totalCaissesNationales: caissesNationales
                });
                setPagination(result.pagination || { total: 0, page: 1, pages: 1 });
            } else {
                setError(result.message || 'Erreur de chargement');
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [currentPage, searchTerm, provinceFilter, itemsPerPage, navigate]);

    useEffect(() => {
        fetchEglises();
    }, [fetchEglises, refreshKey, timestamp]);

    const handleSearch = (e) => {
        e.preventDefault();
        setCurrentPage(1);
        setRefreshKey(prev => prev + 1);
        setTimestamp(Date.now());
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
        setRefreshKey(prev => prev + 1);
        setTimestamp(Date.now());
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDeleteClick = (eglise) => {
        setEgliseToDelete(eglise);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = async () => {
        if (!egliseToDelete) return;
        setDeleting(true);
        try {
            const userStr = localStorage.getItem('user');
            const user = JSON.parse(userStr);
            const userId = user.id || user.id_utilisateur;
            const response = await fetch(`https://josephsbusiness.com/api/eglises.php?user_id=${userId}&id=${egliseToDelete.id}`, {
                method: 'DELETE',
                headers: { 'Accept': 'application/json' }
            });
            const result = await response.json();
            if (result.success) {
                setShowDeleteModal(false);
                setEgliseToDelete(null);
                setRefreshKey(prev => prev + 1);
                setTimestamp(Date.now());
                setSuccessMessage('Église supprimée avec succès');
                setShowSuccessMessage(true);
                setTimeout(() => setShowSuccessMessage(false), 3000);
            } else {
                alert(result.message || 'Erreur lors de la suppression');
            }
        } catch (err) {
            alert('Erreur: ' + err.message);
        } finally {
            setDeleting(false);
        }
    };

    const getProvinceStats = () => {
        const provinceCount = {};
        eglises.forEach(eglise => {
            if (eglise.province) {
                provinceCount[eglise.province] = (provinceCount[eglise.province] || 0) + 1;
            }
        });
        return Object.entries(provinceCount)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 5);
    };

  if (loading && eglises.length === 0) {
    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4 dashboard-styled">
                <div className="loading-wrapper">
                    <div className="text-center">
                        {/* Logo ou icône animé */}
                        {/* <div className="loading-icon-wrapper mb-4">
                            <div className="loading-icon-pulse">
                                <i className="ri-building-line" style={{ fontSize: '3.5rem', color: COLORS.primary }}></i>
                            </div>
                        </div> */}

                        {/* Anneau de chargement amélioré */}
                        <div className="loading-ring-wrapper mb-4">
                            <div className="loading-ring"></div>
                            <div className="loading-ring-inner"></div>
                            <div className="loading-ring-dot"></div>
                        </div>

                        {/* Titre avec effet de gradient */}
                        <h5 className="fw-bold loading-title" style={{ 
                            color: COLORS.primary,
                            background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.secondary})`,
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                            fontSize: '1.4rem'
                        }}>
                            Chargement en cours
                        </h5>

                        {/* Message avec animation de points */}
                        <p className="text-muted small loading-message">
                            Récupération des données
                            <span className="loading-dots">
                                <span>.</span><span>.</span><span>.</span>
                            </span>
                        </p>

                        {/* Barre de progression stylisée */}
                        <div className="loading-progress-wrapper mt-4">
                            <div className="loading-progress-track">
                                <div className="loading-progress-bar"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />

            {/* Styles intégrés pour le loading */}
            <style>{`
                .loading-wrapper {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    min-height: 60vh;
                }

                /* Icône animée */
                .loading-icon-wrapper {
                    position: relative;
                    width: 80px;
                    height: 80px;
                    margin: 0 auto;
                }
                .loading-icon-pulse {
                    animation: iconPulse 1.8s ease-in-out infinite;
                }
                @keyframes iconPulse {
                    0%, 100% { transform: scale(1); opacity: 1; }
                    50% { transform: scale(1.1); opacity: 0.6; }
                }

                /* Anneaux de chargement */
                .loading-ring-wrapper {
                    position: relative;
                    width: 70px;
                    height: 70px;
                    margin: 0 auto;
                }
                .loading-ring {
                    position: absolute;
                    width: 100%;
                    height: 100%;
                    border-radius: 50%;
                    border: 3px solid #e9edf2;
                    border-top-color: ${COLORS.primary};
                    animation: spin 1s cubic-bezier(0.6, 0.2, 0.4, 0.8) infinite;
                }
                .loading-ring-inner {
                    position: absolute;
                    top: 8px;
                    left: 8px;
                    right: 8px;
                    bottom: 8px;
                    border-radius: 50%;
                    border: 2px solid #e9edf2;
                    border-bottom-color: ${COLORS.secondary};
                    animation: spin 0.7s cubic-bezier(0.4, 0.0, 0.2, 1) infinite reverse;
                }
                .loading-ring-dot {
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    width: 12px;
                    height: 12px;
                    border-radius: 50%;
                    background: ${COLORS.primary};
                    box-shadow: 0 0 20px ${COLORS.primary}40;
                    animation: dotPulse 1.2s ease-in-out infinite;
                }
                @keyframes spin {
                    to { transform: rotate(360deg); }
                }
                @keyframes dotPulse {
                    0%, 100% { transform: translate(-50%, -50%) scale(0.8); opacity: 0.6; }
                    50% { transform: translate(-50%, -50%) scale(1.2); opacity: 1; }
                }

                /* Titre avec dégradé (déjà géré en inline) */
                .loading-title {
                    margin-bottom: 0.25rem;
                }

                /* Message avec points animés */
                .loading-message {
                    font-size: 0.9rem;
                    color: #94a3b8;
                }
                .loading-dots span {
                    animation: dots 1.4s infinite;
                    opacity: 0;
                }
                .loading-dots span:nth-child(1) { animation-delay: 0s; }
                .loading-dots span:nth-child(2) { animation-delay: 0.2s; }
                .loading-dots span:nth-child(3) { animation-delay: 0.4s; }
                @keyframes dots {
                    0% { opacity: 0; }
                    50% { opacity: 1; }
                    100% { opacity: 0; }
                }

                /* Barre de progression */
                .loading-progress-wrapper {
                    max-width: 250px;
                    margin: 0 auto;
                }
                .loading-progress-track {
                    width: 100%;
                    height: 4px;
                    background: #e9edf2;
                    border-radius: 4px;
                    overflow: hidden;
                }
                .loading-progress-bar {
                    width: 0%;
                    height: 100%;
                    background: linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary}, ${COLORS.primary});
                    background-size: 200% 100%;
                    border-radius: 4px;
                    animation: progress 2s ease-in-out infinite;
                }
                @keyframes progress {
                    0% { width: 0%; background-position: 200% 0; }
                    50% { width: 70%; background-position: 100% 0; }
                    100% { width: 100%; background-position: 0% 0; }
                }
            `}</style>
        </>
    );
}


    return (
        <>
            <style>{styles}</style>
            <Header />
            <div className="main main-app p-3 p-lg-4 dashboard-styled">
                <div className="container-fluid px-0">

                    {/* En‑tête moderne */}
                    <div className="header-modern mb-4 fade-in">
                        <div className="header-left">
                            <div className="header-icon-wrapper">
                                <i className="ri-building-line"></i>
                            </div>
                            <div>
                                <h5 className="gradient-text">Églises (Sièges)</h5>
                                <div className="d-flex flex-wrap gap-2">
                                    <span className="badge-modern">
                                        <i className="ri-bar-chart-line"></i> Total {stats.total}
                                    </span>
                                    <span className="badge-modern">
                                        <i className="ri-calendar-line"></i> {new Date().toLocaleDateString('fr-FR')}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            <Button size="sm" className="btn-modern-outline" onClick={() => setShowFilters(!showFilters)}>
                                <i className="ri-filter-line"></i> {showFilters ? 'Masquer' : 'Filtres'}
                            </Button>
                            {isSuperAdmin && (
                                <Button size="sm" className="btn-modern-primary" onClick={() => navigate('/eglises/ajouter')}>
                                    <i className="ri-add-line"></i> Ajouter
                                </Button>
                            )}
                        </div>
                    </div>

                    {/* Message de succès */}
                    {showSuccessMessage && (
                        <Alert
                            variant="success"
                            className="border-0 shadow-sm rounded-3 mb-3"
                            onClose={() => setShowSuccessMessage(false)}
                            dismissible
                            style={{ borderRadius: '4px' }}
                        >
                            <div className="d-flex align-items-center">
                                <i className="ri-checkbox-circle-line fs-4 me-2" style={{ color: COLORS.success }}></i>
                                <span>{successMessage}</span>
                            </div>
                        </Alert>
                    )}

                    {/* KPI Cards */}
                    <Row className="g-3 mb-4">
                        <Col xs={6} md={3}>
                            <div className="kpi-card kpi-primary">
                                <div className="kpi-icon"><i className="ri-building-line"></i></div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Églises</span>
                                    <h3 className="kpi-value">{stats.total}</h3>
                                </div>
                            </div>
                        </Col>
                        <Col xs={6} md={3}>
                            <div className="kpi-card kpi-success">
                                <div className="kpi-icon"><i className="ri-building-2-line"></i></div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Temples</span>
                                    <h3 className="kpi-value">{stats.totalTemples}</h3>
                                </div>
                            </div>
                        </Col>
                        <Col xs={6} md={3}>
                            <div className="kpi-card kpi-info">
                                <div className="kpi-icon"><i className="ri-map-pin-line"></i></div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Provinces</span>
                                    <h3 className="kpi-value">{stats.provinces}</h3>
                                </div>
                            </div>
                        </Col>
                        <Col xs={6} md={3}>
                            <div className="kpi-card kpi-warning">
                                <div className="kpi-icon"><i className="ri-bank-line"></i></div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Caisses nationales</span>
                                    <h3 className="kpi-value">{stats.totalCaissesNationales}</h3>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* Statistiques géographiques - avec hauteur égale */}
                    <Row className="g-3 mb-4">
                        <Col md={6}>
                            <Card className="border-0 shadow-sm rounded-3 h-100">
                                <Card.Body className="p-3 d-flex flex-column">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <span className="fw-semibold">Répartition géographique</span>
                                        <Badge style={{ background: COLORS.primary, borderRadius: '4px', color: 'white' }}>{stats.provinces} provinces</Badge>
                                    </div>
                                    <div className="flex-grow-1">
                                        {getProvinceStats().map(([province, count], idx) => {
                                            const percentage = (count / stats.total) * 100;
                                            return (
                                                <div key={idx} className="mb-2">
                                                    <div className="d-flex justify-content-between small">
                                                        <span>{province}</span>
                                                        <span className="fw-semibold">{count}</span>
                                                    </div>
                                                    <div className="progress" style={{ height: '4px', borderRadius: '4px', background: '#e9edf2' }}>
                                                        <div className="progress-bar" style={{ width: `${percentage}%`, background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`, borderRadius: '4px' }}></div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={6}>
                            <Card className="border-0 shadow-sm rounded-3 h-100">
                                <Card.Body className="p-3 d-flex flex-column">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <span className="fw-semibold">Top églises (temples)</span>
                                        <Badge style={{ background: COLORS.success, borderRadius: '4px', color: 'white' }}>{stats.eglisesAvecTemples} actives</Badge>
                                    </div>
                                    <div className="flex-grow-1">
                                        {eglises
                                            .filter(e => (e.nb_filiales || 0) > 0)
                                            .sort((a, b) => (b.nb_filiales || 0) - (a.nb_filiales || 0))
                                            .slice(0, 5)
                                            .map((e, idx) => (
                                                <div key={e.id} className="d-flex justify-content-between align-items-center py-1">
                                                    <div className="d-flex align-items-center">
                                                        <span className="me-2" style={{ width: '20px', fontWeight: '600', color: COLORS.primary }}>#{idx + 1}</span>
                                                        <span className="small">{e.nom}</span>
                                                    </div>
                                                    <Badge style={{ background: COLORS.info, borderRadius: '4px', color: 'white' }}>{e.nb_filiales}</Badge>
                                                </div>
                                            ))}
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* Filtres */}
                    {showFilters && (
                        <Card className="filter-card mb-4 fade-in">
                            <Card.Body className="p-3">
                                <Form onSubmit={handleSearch}>
                                    <Row className="g-2">
                                        <Col md={6}>
                                            <InputGroup size="sm">
                                                <InputGroup.Text style={{ background: 'transparent', border: 'none', color: 'white' }}>
                                                    <i className="ri-search-line"></i>
                                                </InputGroup.Text>
                                                <Form.Control
                                                    placeholder="Rechercher (nom, matricule, ville)"
                                                    value={searchTerm}
                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                />
                                            </InputGroup>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Select
                                                value={provinceFilter}
                                                onChange={(e) => setProvinceFilter(e.target.value)}
                                            >
                                                <option value="">Toutes les provinces</option>
                                                {provinces.map(p => <option key={p} value={p}>{p}</option>)}
                                            </Form.Select>
                                        </Col>
                                        <Col md={2}>
                                            <Button type="submit" size="sm" className="btn-modern-primary w-100">
                                                <i className="ri-filter-3-line me-1"></i> Filtrer
                                            </Button>
                                        </Col>
                                    </Row>
                                </Form>
                            </Card.Body>
                        </Card>
                    )}

                    {/* Tableau */}
                    <Card className="table-card">
                        <div className="table-header-secondary d-flex justify-content-between align-items-center px-3 py-2">
                            <span className="fw-semibold"><i className="ri-list-check me-1"></i> Liste des églises</span>
                            <span className="badge bg-light text-dark rounded-pill px-3">{pagination.total} total</span>
                        </div>
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="mb-0 align-middle table-modern">
                                    <thead>
                                        <tr>
                                            <th className="px-2">Matricule</th>
                                            <th className="px-2">Nom</th>
                                            <th className="px-2">Localisation</th>
                                            <th className="px-2">Temples</th>
                                            <th className="px-2">Statut</th>
                                            <th className="px-2 text-center" style={{ width: '100px' }}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {eglises.length > 0 ? (
                                            eglises.map((eglise) => (
                                                <tr key={eglise.id}>
                                                    <td className="px-2"><span className="reference-badge">{eglise.matricule}</span></td>
                                                    <td className="px-2">
                                                        <div className="d-flex align-items-center gap-2">
                                                            <div className="church-avatar"><i className="ri-building-line"></i></div>
                                                            <div>
                                                                <div className="fw-semibold">{eglise.nom}</div>
                                                                <small className="text-muted">{eglise.email || 'Email non renseigné'}</small>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-2">
                                                        <div>{eglise.ville || '-'}</div>
                                                        <small className="text-muted">{eglise.province || '-'}</small>
                                                    </td>
                                                    <td className="px-2">
                                                        {eglise.nb_filiales > 0 ? (
                                                            <Badge style={{ background: COLORS.info, borderRadius: '4px', color: 'white' }}>
                                                                <i className="ri-building-2-line me-1"></i> {eglise.nb_filiales}
                                                            </Badge>
                                                        ) : <span className="text-muted">-</span>}
                                                    </td>
                                                    <td className="px-2">
                                                        {eglise.caisse === 1 ? (
                                                            <span className='badge bg-warning' style={{ background: '#FDB52F !important', borderRadius: '4px', }}>
                                                                <i className="ri-bank-line me-1"></i> Caisse nationale
                                                            </span>
                                                        ) : (
                                                            <Badge style={{ background: COLORS.primary, borderRadius: '4px', color: 'white' }}>
                                                                Église locale
                                                            </Badge>
                                                        )}
                                                    </td>
                                                    <td className="px-2 text-center">
                                                        <div className="d-flex gap-1 justify-content-center">
                                                            <button className="action-btn action-btn-view" title="Voir" onClick={() => navigate(`/eglises/${eglise.id}`)}>
                                                                <i className="ri-eye-line"></i>
                                                            </button>
                                                            {(isSuperAdmin || isAdmin) && (
                                                                <button className="action-btn action-btn-edit" title="Modifier" onClick={() => navigate(`/eglises/modifier/${eglise.id}`)}>
                                                                    <i className="ri-pencil-line"></i>
                                                                </button>
                                                            )}
                                                            {isSuperAdmin && (
                                                                <button className="action-btn action-btn-delete" title="Supprimer" onClick={() => handleDeleteClick(eglise)} disabled={eglise.nb_filiales > 0}>
                                                                    <i className="ri-delete-bin-line"></i>
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="6" className="text-center py-4">
                                                    <i className="ri-inbox-line" style={{ fontSize: '2rem', color: '#94a3b8', display: 'block', marginBottom: '0.5rem' }}></i>
                                                    <p className="text-muted small">Aucune église trouvée</p>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                        {pagination.pages > 1 && (
                            <Card.Footer className="border-0 bg-transparent pt-2 pb-3">
                                <div className="d-flex justify-content-between align-items-center">
                                    <small className="text-muted">Page {currentPage} / {pagination.pages}</small>
                                    <Pagination className="pagination-modern mb-0">
                                        <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
                                        <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                                        {[...Array(Math.min(5, pagination.pages))].map((_, i) => {
                                            let pageNum;
                                            if (pagination.pages <= 5) pageNum = i + 1;
                                            else if (currentPage <= 3) pageNum = i + 1;
                                            else if (currentPage >= pagination.pages - 2) pageNum = pagination.pages - 4 + i;
                                            else pageNum = currentPage - 2 + i;
                                            return (
                                                <Pagination.Item key={i} active={pageNum === currentPage} onClick={() => handlePageChange(pageNum)}>
                                                    {pageNum}
                                                </Pagination.Item>
                                            );
                                        })}
                                        <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === pagination.pages} />
                                        <Pagination.Last onClick={() => handlePageChange(pagination.pages)} disabled={currentPage === pagination.pages} />
                                    </Pagination>
                                </div>
                            </Card.Footer>
                        )}
                    </Card>

                    <Footer />
                </div>
            </div>

        {/* Modal de suppression moderne */}
<Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered className="delete-modal">
    <Modal.Header 
        closeButton 
        className="border-0 pb-0" 
        style={{ 
            background: `linear-gradient(135deg, ${COLORS.danger}, #dc2626)`,
            borderTopLeftRadius: '4px',
            borderTopRightRadius: '4px'
        }}
    >
        <Modal.Title className="w-100 text-center">
            <div className="delete-icon-wrapper mb-2">
                <div className="delete-icon-circle" style={{
                    width: '70px',
                    height: '70px',
                    borderRadius: '4px',
                    background: 'rgba(255,255,255,0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto'
                }}>
                    <i className="ri-delete-bin-line" style={{ fontSize: '32px', color: 'white' }}></i>
                </div>
            </div>
            <h5 className="fw-bold text-white mb-0">Confirmer la suppression</h5>
            <p className="text-white-50 small mb-0">Cette action est irréversible</p>
        </Modal.Title>
    </Modal.Header>

    <Modal.Body className="pt-4 px-4">
        {egliseToDelete && (
            <>
                <p className="text-center text-muted mb-3">
                    Êtes-vous sûr de vouloir supprimer définitivement cette église ?
                </p>

                {/* Carte d'information de l'église */}
                <div className="p-3 mb-3" style={{
                    background: '#f8fafc',
                    borderLeft: `3px solid ${COLORS.primary}`,
                    borderRadius: '4px'
                }}>
                    <div className="d-flex align-items-center gap-3">
                        <div className="church-avatar" style={{
                            width: '48px',
                            height: '48px',
                            borderRadius: '4px',
                            background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: '1.2rem',
                            flexShrink: 0
                        }}>
                            <i className="ri-building-line"></i>
                        </div>
                        <div>
                            <div className="fw-bold" style={{ color: COLORS.primaryDark }}>
                                {egliseToDelete.nom}
                            </div>
                            <div className="text-muted small">
                                <i className="ri-barcode-line me-1"></i>
                                Matricule: {egliseToDelete.matricule}
                            </div>
                            <div className="text-muted small">
                                <i className="ri-map-pin-line me-1"></i>
                                {egliseToDelete.ville || 'Ville non renseignée'}
                            </div>
                            {egliseToDelete.nb_filiales > 0 && (
                                <div className="text-danger small mt-1">
                                    <i className="ri-alert-line me-1"></i> 
                                    {egliseToDelete.nb_filiales} temple(s) rattaché(s)
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Avertissement */}
                <div style={{
                    background: `${COLORS.danger}10`,
                    border: `1px solid ${COLORS.danger}30`,
                    borderRadius: '4px',
                    padding: '0.8rem 1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.8rem'
                }}>
                    <i className="ri-alert-line" style={{ color: COLORS.danger, fontSize: '1.3rem' }}></i>
                    <span className="small text-muted">
                        Toutes les données associées à cette église seront définitivement supprimées.
                        {egliseToDelete.nb_filiales > 0 && " Vous ne pouvez pas supprimer une église avec des temples rattachés."}
                    </span>
                </div>
            </>
        )}
    </Modal.Body>

    <Modal.Footer className="border-0 pt-0 pb-4 px-4 justify-content-center gap-2">
        <Button 
            variant="light" 
            onClick={() => setShowDeleteModal(false)} 
            disabled={deleting}
            className="px-4"
            style={{
                borderRadius: '4px',
                padding: '0.5rem 1.5rem',
                border: '1px solid #d1d5db',
                color: '#4b5563',
                fontWeight: '500',
                fontSize: '0.85rem',
                transition: 'all 0.2s',
                background: 'white'
            }}
        >
            Annuler
        </Button>
        <Button
            variant="danger"
            onClick={handleDeleteConfirm}
            disabled={deleting || (egliseToDelete?.nb_filiales > 0)}
            className="px-4"
            style={{
                background: `linear-gradient(135deg, ${COLORS.danger}, #dc2626)`,
                border: 'none',
                borderRadius: '4px',
                padding: '0.5rem 1.5rem',
                fontWeight: '500',
                fontSize: '0.85rem',
                boxShadow: '0 4px 10px rgba(239, 68, 68, 0.25)',
                transition: 'all 0.2s',
                color: 'white'
            }}
            onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 8px 16px rgba(239, 68, 68, 0.35)';
            }}
            onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 10px rgba(239, 68, 68, 0.25)';
            }}
        >
            {deleting ? (
                <><Spinner size="sm" className="me-1" /> Suppression...</>
            ) : (
                <><i className="ri-delete-bin-line me-2"></i>Oui, supprimer</>
            )}
        </Button>
    </Modal.Footer>
</Modal>
        </>
    );
}